import { Link } from '@tanstack/react-router';
import { CircleArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { TagBulkChargeFamilyPreview } from '~entities/payment/api/bulk-charge-by-tags';

import { ChargeDetailsDialog } from '~features/payment/ui/charge-details-dialog';
import { ConfirmationStep } from '~features/payment/ui/confirmation-step';
import { StudentSelectionStep } from '~features/payment/ui/student-selection-step';
import { TagChargeDetailsDialog } from '~features/payment/ui/tag-charge-details-dialog';
import { TagFamilyConfirmationStep } from '~features/payment/ui/tag-family-confirmation-step';

import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

export interface Student {
  _id: string;
  name: string;
  familyName?: string;
}

export const BulkChargeStudents = () => {
  useEffect(() => {
    if (!document.getElementById('custom-animations')) {
      const style = document.createElement('style');
      style.id = 'custom-animations';
      style.innerHTML = `
        @keyframes slideDownAndFade {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .animate-slide-down {
          animation: slideDownAndFade 0.2s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const [step, setStep] = useState<'selection' | 'confirmation'>('selection');
  const [flow, setFlow] = useState<'students' | 'tags'>('students');
  const [selectedStudents, setSelectedStudents] = useState<Array<Student>>([]);
  const [selectedFamilies, setSelectedFamilies] = useState<
    Array<TagBulkChargeFamilyPreview>
  >([]);
  const [showChargeDialog, setShowChargeDialog] = useState(false);

  const handleStudentsSelected = (students: Array<Student>) => {
    setFlow('students');
    setSelectedFamilies([]);
    setSelectedStudents(students);
    setStep('confirmation');
  };

  const handleFamiliesSelected = (
    families: Array<TagBulkChargeFamilyPreview>,
  ) => {
    setFlow('tags');
    setSelectedStudents([]);
    setSelectedFamilies(families);
    setStep('confirmation');
  };

  const handleBack = () => {
    setStep('selection');
  };

  const handleConfirm = () => {
    setShowChargeDialog(true);
  };

  const handleChargeComplete = () => {
    setShowChargeDialog(false);
  };

  return (
    <div className=''>
      <Link to='/dashboard' className='flex items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <h2 className='text-2xl font-semibold'>Bulk Charge Students</h2>
      </Link>
      <div className='mt-8 border-t'>
        <div className='container max-w-5xl space-y-6 py-6'>
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end'>
            <div className='flex items-center space-x-2'>
              <Button variant='outline' className='w-full [&_svg]:size-5 sm:w-auto' asChild>
                <Link to='/payment/class-charge-history'>
                  Class Charge History
                </Link>
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Bulk Charge Students</CardTitle>
              <CardDescription>
                Charge multiple students at once by selecting them manually, by
                class, by group, or by tag (once per family).
              </CardDescription>
            </CardHeader>
            <CardContent>
              {step === 'selection' ? (
                <StudentSelectionStep
                  onStudentsSelected={handleStudentsSelected}
                  onFamiliesSelected={handleFamiliesSelected}
                />
              ) : flow === 'tags' ? (
                <TagFamilyConfirmationStep families={selectedFamilies} />
              ) : (
                <ConfirmationStep students={selectedStudents} />
              )}
            </CardContent>
            <CardFooter className='flex justify-between'>
              {step === 'confirmation' && (
                <Button variant='outline' onClick={handleBack}>
                  Back
                </Button>
              )}
              {step === 'confirmation' && (
                <Button onClick={handleConfirm}>Proceed to Charge</Button>
              )}
            </CardFooter>
          </Card>

          {flow === 'students' ? (
            <ChargeDetailsDialog
              open={showChargeDialog}
              onOpenChange={setShowChargeDialog}
              students={selectedStudents}
              onComplete={handleChargeComplete}
            />
          ) : (
            <TagChargeDetailsDialog
              open={showChargeDialog}
              onOpenChange={setShowChargeDialog}
              families={selectedFamilies}
              onComplete={handleChargeComplete}
            />
          )}
        </div>
      </div>
    </div>
  );
};
